import React from 'react';
import { Phone, MessageSquare, User } from 'lucide-react';
import { PremiumBadge } from './PremiumBadge';

/**
 * Contact card shown on an active delivery to both customer & driver AFTER
 * payment has been authorized. Provides click-to-call + WhatsApp open links.
 *
 * Props:
 *   name:    person's name
 *   phone:   E.164-ish phone string
 *   role:    'Driver' | 'Customer' (label only)
 *   subtitle?: optional 2nd-line context (e.g. vehicle type)
 *   isPremium?: when true (driver only), shows the Premium Driver badge
 */
export const ContactCard = ({ name, phone, role, subtitle, isPremium, dataTestId }) => {
  if (!phone && !name) return null;
  const cleanPhone = (phone || '').replace(/[^\d+]/g, '');
  // For WhatsApp the international country code is required without the leading +.
  // We assume Irish numbers default to +353 if the number starts with "0".
  let waPhone = cleanPhone;
  if (waPhone.startsWith('+')) waPhone = waPhone.slice(1);
  else if (waPhone.startsWith('00')) waPhone = waPhone.slice(2);
  else if (waPhone.startsWith('0')) waPhone = `353${waPhone.slice(1)}`;

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 sm:p-4 space-y-3" data-testid={dataTestId}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Your {role}</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-bold text-gray-900 truncate" data-testid={`${dataTestId}-name`}>{name || '—'}</p>
            {isPremium && <PremiumBadge size="sm" dataTestId={`${dataTestId}-premium-badge`} />}
          </div>
          {subtitle && <p className="text-xs text-gray-600 truncate">{subtitle}</p>}
          {phone && <p className="text-sm font-mono text-gray-700 tracking-wide" data-testid={`${dataTestId}-phone`}>{phone}</p>}
        </div>
      </div>
      {phone && (
        <div className="grid grid-cols-2 gap-2">
          <a
            href={`tel:${cleanPhone}`}
            className="inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md bg-[#2E7D32] text-white text-sm font-semibold hover:bg-[#1B5E20]"
            data-testid={`${dataTestId}-call-btn`}
          >
            <Phone className="w-4 h-4" /> Call
          </a>
          <a
            href={`https://wa.me/${waPhone}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-md bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1ebe57]"
            data-testid={`${dataTestId}-whatsapp-btn`}
          >
            <MessageSquare className="w-4 h-4" /> WhatsApp
          </a>
        </div>
      )}
    </div>
  );
};

export default ContactCard;
